import { node, icon, createPremiumIcon } from "~/util/dom";
import { _ } from "~/i18n";
import { MONTHS_ABBREVIATIONS } from "~/activities/constants";

type TDate = number | null;

interface ISubDataItem {
	from: TDate;
	id: string;
	text: string;
	to: TDate;
	value: number;
}

interface IDataItem {
	from?: TDate;
	to?: TDate;
	// all | 2024...
	id: string;
	items: Array<ISubDataItem>;
	text: string;
}

interface IInputData {
	// roky
	[key: string]: {
		// mesice 1-12, opt a jejich pocet
		[key: string]: number;
	};
}

type TSelType = "all" | "more-years" | "one-year" | "year-months";

export interface IMyMapsSeasonChartsFilterData {
	isAll: boolean;
	fromDate: TDate;
	toDate: TDate;
	type: TSelType;
	title: string;
}

interface ISelectorInterval {
	min: number;
	max: number;
	pos: number;
}

interface ISeasonItem {
	item: HTMLSpanElement;
	top: HTMLSpanElement;
	count: null | HTMLSpanElement;
}

interface IOpts {
	data?: IInputData;
	isPremiumUser?: boolean;
	selAllYears?: boolean;
	minGraphHeight?: number;
	onChange?: (selDatesData: IMyMapsSeasonChartsFilterData) => void;
	onNonPremiumClick?: () => void;
}

const ALL_SEASONS = "all";

function prepareSummaryData(data: IInputData, setAllYears = true) {
	const chartsData: Array<IDataItem> = [{
		items: [],
		text: "all",
		id: "all",
		from: null,
		to: null,
	}];

	if (!data) {
		return chartsData;
	}

	let allYears = Object.keys(data).map(item => parseInt(item));

	allYears.sort((aItem: number, bItem: number) => aItem - bItem);

	if (setAllYears && allYears.length > 1) {
		const minYear = allYears[0];
		const maxYear = allYears[allYears.length - 1];
		const newYears: Array<number> = [];

		for (let curYear = minYear; curYear <= maxYear; curYear++) {
			newYears.push(curYear);
		}

		allYears = newYears;
	}

	for (const year of allYears) {
		const yearStr = year.toString();

		chartsData.push({
			items: [],
			text: yearStr,
			id: yearStr,
		});
		const chart = chartsData[chartsData.length - 1];
		let value = 0;

		const months = data[yearStr];

		MONTHS_ABBREVIATIONS.forEach((text, key) => {
			const month = key + 1;

			value += months && month in months ? months[month] : 0;
			chart.items.push({
				text: _(`calendar.shortMonths.${text}`),
				id: month.toString(),
				value: months && month in months ? months[month] : 0,
				from: new Date(year, month - 1, 1, 0, 0, 0).getTime(),
				to: new Date(year, month - 1, new Date(year, month, 0).getDate(), 23, 59, 59).getTime(),
			});
		});

		chartsData[0].items.push({
			text: yearStr,
			id: yearStr,
			value,
			from: new Date(year, 0, 1, 0, 0, 0).getTime(),
			to: new Date(year, 11, 31, 23, 59, 59).getTime(),
		});
	}

	return chartsData;
}

/**
 * cele obdobi - nic nezobrazujeme,
 * rok - cely, vcetne prazdnych mesicu
 */
export default class MyMapsFilterSeasonChart {
	_opts: IOpts;

	_data: Array<IDataItem>;

	_contWidth: number;

	_seasonsLinks: {[key: string]: HTMLAnchorElement };

	_isAll: boolean;

	_resizeObserver: ResizeObserver | null;

	_dom: {
		container: HTMLDivElement;
		list: HTMLDivElement;
		seasonPicker: HTMLDivElement;
		buttons: HTMLDivElement;
		selector: HTMLDivElement;
		minCircle: HTMLSpanElement;
		maxCircle: HTMLSpanElement;
		items: HTMLDivElement;
		item: Array<any>;
		resetBtn: HTMLButtonElement;
		doneBtn: HTMLMapyRouterButtonElement;
	};

	_season: string;

	_activeId: number;

	_prevItemsHeight: Array<number>;

	_selectorInterval: Array<ISelectorInterval>;

	_seasonPickerBBox: DOMRect;

	_separators: Array<ISeasonItem>;

	_from: number;

	_to: number;

	_tmp: null | {
		from: number;
		to: number;
	};

	_moveElement: null | HTMLSpanElement;

	constructor(opts: IOpts) {
		this._opts = {
			// [%] misto nuly min pocet procent pro zobrazeni prazdnych mesicu
			minGraphHeight: 5,
			data: null,
			isPremiumUser: false,
			selAllYears: true,
			onChange: () => {},
			onNonPremiumClick: () => {},
			...opts,
		};

		// styly nechame
		this._dom = {
			container: node("div", { className: "mymaps__filterSeasonChart" }),
			list: node("div", { className: "mymaps__filterSeasonChart__list" }),
			seasonPicker: node("div", { className: "mymaps__filterSeasonChart__seasonPicker" }),
			buttons: node("div", { className: "mymaps__filterSeasonChart__buttons" }),
			selector: node("div", { className: "selector" }),
			minCircle: node("span", { className: "circle min" }),
			maxCircle: node("span", { className: "circle max" }),
			items: node("div", { className: "items" }),
			item: [],
			resetBtn: node("button", { type: "button", className: "mymaps__filterSeasonChart__resetBtn" }, _("mymap.filters.resetFilters")),
			doneBtn: node("mapy-router-button", {}, _("done")),
		};
		this._dom.container.append(this._dom.list, this._dom.seasonPicker, this._dom.buttons);
		this._dom.buttons.append(this._dom.resetBtn, this._dom.doneBtn);

		this._data = prepareSummaryData(this._opts.data, this._opts.selAllYears);

		this._activeId = 0;
		this._prevItemsHeight = [];
		this._selectorInterval = [];
		this._seasonPickerBBox = null;
		this._separators = [];
		this._moveElement = null;
		this._from = 0;
		this._to = 0;
		this._tmp = null;
		this._season = ALL_SEASONS;
		this._contWidth = this._dom.seasonPicker.offsetWidth;
		this._seasonsLinks = {};
		this._isAll = true;
		this._resizeObserver = null;

		if (!this._opts.isPremiumUser) {
			this._dom.list.addEventListener("click", () => {
				this._opts.onNonPremiumClick();
			});
			this._dom.seasonPicker.addEventListener("click", () => {
				this._opts.onNonPremiumClick();
			});
			this._dom.container.classList.add("non-premium");
		}

		this._create();
		this._setSeasons();
		this.show = this._season;

		if (this._contWidth === 0) {
			this._resizeObserver = new ResizeObserver(entries => {
				if (entries.length === 1) {
					const width = Math.floor(entries[0].contentRect.width);

					if (width > 0) {
						this._resizeObserver.disconnect();
						this._resizeObserver = null;

						this._contWidth = width;
						this._setSelectorInterval();
						this._setInterval();
					}
				}
			});

			this._resizeObserver.observe(this._dom.seasonPicker);
		}
	}

	destroy() {
		this._dom.selector.removeEventListener("pointerdown", this);
		window.removeEventListener("resize", this, false);
		this._resizeObserver?.disconnect();
	}

	get container() {
		return this._dom.container;
	}

	set data(inputData: IInputData) {
		this._data = prepareSummaryData(inputData, this._opts.selAllYears);
		this._setSeasons();
	}

	reset() {
		this.show = ALL_SEASONS;
	}

	_setData(id: string, range?: [number, number]) {
		let data = this._getChartData(id);

		this._season = id;
		this._from = 0;
		this._to = 0;

		if (!Object.keys(data).length) {
			data = this._buildEmptyItem(id);
		}

		this._createSeasonPicker(range);
		this._setInterval();
		this._setIsAll();

		const allSeasons = Object.keys(this._seasonsLinks);

		for (const season of allSeasons) {
			this._seasonsLinks[season].classList[season === this._season ? "add" : "remove"]("active");
		}
	}

	set show(id: string) {
		this._setData(id);
	}

	set selDatesData(selDatesData: IMyMapsSeasonChartsFilterData) {
		let id = ALL_SEASONS;
		let range: [number, number] = null;
		const fromDate = new Date(selDatesData.fromDate);
		const toDate = new Date(selDatesData.toDate);

		if (!(["all", "more-years"] as Array<TSelType>).includes(selDatesData.type)) {
			id = fromDate.getFullYear().toString();
		}

		const data = this._getChartData(id);

		if (selDatesData.type === "more-years") {
			const fromYear = fromDate.getFullYear().toString();
			const toYear = toDate.getFullYear().toString();
			const fromInd = data.items.findIndex(item => item.text === fromYear);
			const toInd = data.items.findIndex(item => item.text === toYear);

			range = [fromInd, toInd + 1];
		} else if (selDatesData.type === "year-months") {
			const fromInd = fromDate.getMonth();
			const toInd = toDate.getMonth();

			range = [fromInd, toInd + 1];
		}

		this._setData(id, range);
	}

	get fromToSetted() {
		if (!this._data) {
			return false;
		}

		const seasonData = this._getChartData(this._season);
		const items = Object.keys(seasonData).length ? seasonData.items : this._buildEmptyItem().items;

		return this._from !== 0 || (this._to !== items.length && this._to !== 0);
	}

	get filter(): IMyMapsSeasonChartsFilterData {
		let from: TDate = null;
		let to: TDate = null;
		let data = this._data[this._activeId];
		let type: TSelType = "all";
		let title = _("mymap.activity.filter.wholeSeason");

		if (!data) {
			data = this._buildEmptyItem();
		}

		if (data) {
			from = data.items[this._from] ? data.items[this._from].from : null;
			to = data.items[this._to-1] ? data.items[this._to-1].to : null;
		}

		const fromObj = from ? new Date(from) : new Date();
		const toObj = to ? new Date(to) : new Date();

		if (!this._isAll) {
			const fromYear = fromObj.getFullYear();
			const toYear = toObj.getFullYear();

			if (fromYear === toYear) {
				type = fromObj.getMonth() === 0 && toObj.getMonth() === 11
					? "one-year"
					: "year-months";

				if (type === "one-year") {
					title = `${fromYear}`;
				} else {
					const fromMonth = new Intl.DateTimeFormat("cs-CZ", { month: "short" }).format(fromObj);
					const toMonth = new Intl.DateTimeFormat("cs-CZ", { month: "short" }).format(toObj);

					title = `${fromMonth}-${toMonth} ${fromYear}`;
				}
			} else {
				type = "more-years";
				title = `${fromYear}-${toYear}`;
			}
		}

		return {
			isAll: this._isAll,
			fromDate: from,
			toDate: to,
			type,
			title,
		};
	}

	redraw() {
		const data = this._getChartData(this._season);
		const items = Object.keys(data).length ? data.items : this._buildEmptyItem().items;
		const actualItemsHeight = [];
		let max = 0;

		this._separators = [];
		this._dom.item = [];
		this._dom.items.innerHTML = "";

		for (let ind = 0; ind <= items.length; ind++) {
			let itemData = this._createSeasonItem(true);

			this._dom.items.appendChild(itemData.item);
			this._separators.push(itemData);

			if (ind > 0 && ind < items.length) {
				itemData = this._createSeasonItem(true);
				this._dom.items.appendChild(itemData.item);
				this._separators.push(itemData);
			}

			if (ind >= 0 && ind < items.length) {
				itemData = this._createSeasonItem(false, items[ind].text, items[ind].id);

				this._dom.items.appendChild(itemData.item);
				this._dom.item[ind] = itemData;

				if (items.length === this._prevItemsHeight.length) {
					this._dom.item[ind].count.style.maxHeight = `${this._prevItemsHeight[ind]}%`;
				} else {
					this._dom.item[ind].count.style.maxHeight = `${this._opts.minGraphHeight}%`;
				}

				max = Math.max(max, items[ind].value);
			}
		}

		requestAnimationFrame(() => {
			this._setSelectorInterval();
			this._updateSelected();
			this._setInterval();
		});

		setTimeout(() => {
			for (let ind = 0; ind < items.length; ind++) {
				const value = items[ind].value;
				const height = max === 0 ? 0 : Math.round(100 * value / max);

				this._dom.item[ind].count.style.maxHeight = `${Math.max(height, this._opts.minGraphHeight)}%`;
				actualItemsHeight.push(Math.max(height, this._opts.minGraphHeight));
			}

			this._prevItemsHeight = actualItemsHeight;
		}, 100);
	}

	handleEvent(event) {
		switch (event.type) {
			case "pointerdown":
				event.stopPropagation();
				event.preventDefault();

				this._tmp = {
					from: this._from,
					to: this._to,
				};

				if (this._dom.minCircle.contains(event.target) || this._dom.maxCircle.contains(event.target)) {
					this._dom.selector.addEventListener("pointermove", this);
					document.addEventListener("pointerup", this);
					this._moveElement = this._dom.minCircle.contains(event.target) ? this._dom.minCircle : this._dom.maxCircle;
				} else {
					this._moveElement = this._seasonPickerBBox.width / (event.clientX - this._seasonPickerBBox.left) >= 2 ? this._dom.minCircle : this._dom.maxCircle;
					this._findInterval(event);
					this._updateSelected();
				}

				this._setIsAll();
				break;

			case "pointermove":
				event.stopPropagation();
				event.preventDefault();

				this._findInterval(event);
				this._updateSelected();
				break;

			case "pointerup":
				event.stopPropagation();
				event.preventDefault();

				this._dom.selector.removeEventListener("pointermove", this);
				document.removeEventListener("pointerup", this);
				this._moveElement = null;

				if (this._tmp.from !== this._from || this._tmp.to !== this._to) {
					Mapy.getComponent("logger").log("casovy_filtr_interakce", {
						setting: "osa-usek",
					});
				}

				this._tmp = null;
				this._setIsAll();
				break;

			case "resize":
				if (this._separators.length && this._dom.seasonPicker.offsetWidth > 0) {
					requestAnimationFrame(() => {
						this._setSelectorInterval();
					});
				}
				break;

			default:
		}
	}

	_create() {
		this._dom.seasonPicker.appendChild(this._dom.items);
		this._dom.minCircle.setAttribute("touch-action", "none");
		this._dom.minCircle.appendChild(icon("mover"));
		this._dom.maxCircle.appendChild(icon("mover"));
		this._dom.maxCircle.setAttribute("touch-action", "none");
		this._dom.selector.appendChild(this._dom.minCircle);
		this._dom.selector.appendChild(this._dom.maxCircle);

		window.addEventListener("resize", this, false);

		if (this._opts.isPremiumUser) {
			this._dom.selector.addEventListener("pointerdown", this);
		}

		this._dom.seasonPicker.appendChild(this._dom.selector);
		this._dom.resetBtn.addEventListener("click", () => {
			this.reset();
		});
		this._dom.doneBtn.addEventListener("click", () => {
			this._done();
		});
	}

	_getChartData(id: string) {
		let data: IDataItem = {
			id: "",
			items: [],
			text: "",
		};

		for (let ind = 0; ind < this._data.length; ind++) {
			if (this._data[ind].id === id.toString()) {
				data = this._data[ind];
				this._activeId = ind;
				break;
			}
		}

		return data;
	}

	_createSeasonPicker(range?: [number, number]) {
		const seasonData = this._getChartData(this._season);
		const items = Object.keys(seasonData).length ? seasonData.items : this._buildEmptyItem().items;

		this._from = 0;
		this._to = items.length;

		if (range) {
			this._from = range[0];
			this._to = Math.min(items.length, range[1]);
		}

		this.redraw();
	}

	_seasonItemClick(id: string) {
		const isAllSeasons = this._season === ALL_SEASONS;

		if (isAllSeasons) {
			this.show = id;

			Mapy.getComponent("logger").log("casovy_filtr_interakce", {
				setting: "osa-obdobi",
			});
		} else {
			const month = parseInt(id, 10) - 1;
			const minMonth = 0;
			const maxMonth = 12;

			if (this._from === minMonth) {
				this._from = month;
			} else if (this._to === maxMonth) {
				this._to = month;
			} else {
				// blizsi
				const minDist = Math.abs(month - this._from);
				const maxDist = Math.abs(this._to - month);

				if (minDist < maxDist) {
					this._from = month;
				} else {
					this._to = month;
				}
			}

			if (this._from > this._to) {
				const orig = this._from;

				this._from = this._to;
				this._to = orig;
			}

			this._updateSelected();
			this._setInterval();

			Mapy.getComponent("logger").log("casovy_filtr_interakce", {
				setting: "osa-mesic",
			});
		}
	}

	_createSeasonItem(isSeparator: boolean, cpt?: string, id?: string): ISeasonItem {
		const item = node("span", { className: "item" });
		const top = node("span", { className: "top" });
		const line = node("span", { className: "line" });
		const caption = node("span", { className: "caption", innerHTML: cpt || "" });
		let count = null;

		if (isSeparator) {
			item.classList.add("separator");
		} else {
			const isAllSeasons = this._season === ALL_SEASONS;

			count = node("span", { className: "count" });
			top.appendChild(count);

			if (isAllSeasons) {
				caption.setAttribute("data-item-season", id);
			}
		}

		if (!isSeparator && this._opts.isPremiumUser) {
			item.addEventListener("click", () => {
				this._seasonItemClick(id);
			});
		}

		item.appendChild(top);
		item.appendChild(line);
		item.appendChild(caption);

		return {
			item,
			top,
			count,
		};
	}

	_updateSelected() {
		const separators = this._separators;

		for (let ind = 0; ind < this._dom.item.length; ind++) {
			const action = ind >= this._from && ind < this._to ? "add" : "remove";

			separators[ind * 2].item.classList[action]("active");
			separators[(ind * 2) + 1].item.classList[action]("active");
			this._dom.item[ind].item.classList[action]("active");
			this._dom.item[ind].item.classList.remove("item-left");
			this._dom.item[ind].item.classList.remove("item-right");
		}

		const toInd = this._to - 1;

		this._dom.item[this._from]?.item.classList.add("item-left");
		this._dom.item[toInd]?.item.classList.add("item-right");
	}

	_setSelectorInterval() {
		const separators = this._separators;

		if (this._contWidth === 0) {
			return;
		}

		this._seasonPickerBBox = this._dom.seasonPicker.getBoundingClientRect();
		this._selectorInterval = [];

		this._selectorInterval.push({
			min: 0,
			max: separators[0].item.offsetWidth,
			pos: 0,
		});

		if (separators.length > 2) {
			for (let ind = 1; ind < this._dom.item.length; ind++) {
				const position = separators[ind * 2].item.offsetLeft;

				this._selectorInterval.push({
					min: position-separators[ind * 2].item.offsetWidth,
					max: position+separators[ind * 2].item.offsetWidth,
					pos: position,
				});
			}
		}

		this._dom.selector.style.width = `${this._contWidth}px`;

		this._selectorInterval.push({
			min: this._contWidth - separators[separators.length-1].item.offsetWidth,
			max: this._contWidth,
			pos: this._contWidth,
		});
	}

	_findInterval(event: PointerEvent) {
		const position = event.clientX - this._seasonPickerBBox.left;

		for (let ind = 0; ind < this._selectorInterval.length; ind++) {
			const interval = this._selectorInterval[ind];

			if (position > interval.min && position < interval.max) {
				if ((ind === 0 || this._from >= ind) && this._moveElement === this._dom.maxCircle) {
					break;
				}

				if ((ind === this._selectorInterval.length-1 || this._to <= ind) && this._moveElement === this._dom.minCircle) {
					break;
				}

				this._moveElement.style.left = `${interval.pos}px`;

				if (this._moveElement === this._dom.minCircle) {
					this._from = ind;
				} else {
					this._to = ind;
				}
			}
		}
	}

	_setInterval() {
		if (this._selectorInterval.length === 0) {
			return;
		}

		const len = this._selectorInterval.length - 1;
		const fromInd = Math.max(0, Math.min(this._from, len));
		const toInd = Math.max(0, Math.min(this._to, len));

		this._dom.minCircle.style.left = `${this._selectorInterval[fromInd].pos}px`;
		this._dom.maxCircle.style.left = `${this._selectorInterval[toInd].pos}px`;
	}

	_buildEmptyItem(id = this._activeId.toString()) {
		const idStr = id.toString();
		const data: IDataItem = {
			items: [],
			text: idStr,
			id: idStr,
		};

		MONTHS_ABBREVIATIONS.forEach((text, kInd) => {
			const month = kInd + 1;

			data.items.push({
				text: _(`calendar.shortMonths.${text}`),
				id: month.toString(),
				value: 0,
				from: new Date(parseInt(idStr), month - 1, 1, 0, 0, 0).getTime(),
				to: new Date(parseInt(idStr), month - 1, new Date(parseInt(idStr), month, 0).getDate(), 23, 59, 59).getTime(),
			});
		});

		return data;
	}

	_setSeasons() {
		this._dom.list.innerHTML = "";
		this._seasonsLinks = {};

		for (const seasonItem of this._data) {
			const item = node("a");

			item.classList.add("mymaps__filterSeasonChart__listItem");
			item.innerHTML = seasonItem.text === ALL_SEASONS
				? _("mymap.activity.filter.wholeSeason")
				: seasonItem.text;
			item.setAttribute("data-season", seasonItem.id);
			item.setAttribute("data-log-ignore", "1");
			item.addEventListener("click", () => {
				if (this._opts.isPremiumUser) {
					this.show = seasonItem.id;
				}
			});

			if (!this._opts.isPremiumUser) {
				item.classList.add("disabled");
			}

			this._seasonsLinks[seasonItem.id] = item;
			this._dom.list.append(item);
		}

		if (!this._opts.isPremiumUser) {
			const premiumIcon = createPremiumIcon();

			premiumIcon.classList.add("mymaps__filterSeasonChart__listItemPremium");
			this._dom.list.append(premiumIcon);
		}
	}

	_setIsAll() {
		const data = this._getChartData(this._season);

		this._isAll = this._season === ALL_SEASONS && this._from === 0 && this._to === data.items.length;
		this._dom.resetBtn.classList[this._isAll ? "add" : "remove"]("hide");
	}

	_done() {
		this._opts.onChange(this.filter);
	}
}
